/** One line of a unified patch, numbered on the side(s) it exists on. */
export type PatchRow = {
  /** Zero-based index of the `@@` hunk the row belongs to; unchanged lines between hunks are absent. */
  hunk: number;
  oldLine: number | null;
  newLine: number | null;
  /** The patch line including its `+`, `-` or space prefix. */
  text: string;
};

const HUNK_HEADER_PATTERN = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/;

/**
 * Numbers every line of a unified patch (such as GitHub's `file.patch`) with its old- and
 * new-file line: removed lines exist only on the old side, added lines only on the new side.
 */
export function parsePatchRows(patch: string): PatchRow[] {
  const rows: PatchRow[] = [];
  let hunk = -1;
  let oldLine = 0;
  let newLine = 0;

  for (const text of patch.split('\n')) {
    const hunkHeader = HUNK_HEADER_PATTERN.exec(text);
    if (hunkHeader) {
      hunk += 1;
      oldLine = Number(hunkHeader[1]);
      newLine = Number(hunkHeader[2]);
      continue;
    }

    // Skip file headers, the trailing empty line and "\ No newline at end of file".
    if (hunk === -1 || text === '' || text.startsWith('\\')) {
      continue;
    }

    if (text.startsWith('+')) {
      rows.push({ hunk, oldLine: null, newLine, text });
      newLine += 1;
    } else if (text.startsWith('-')) {
      rows.push({ hunk, oldLine, newLine: null, text });
      oldLine += 1;
    } else {
      rows.push({ hunk, oldLine, newLine, text });
      oldLine += 1;
      newLine += 1;
    }
  }

  return rows;
}
